import { createContext, useContext } from 'react'
import { TFollowup } from 'src/types/followup'
import GenericFollowupActions from './generic-followup-actions.component'
import GenericFollowupHeader from './generic-followup-header.component'
import GenericFollowupVariables from './generic-followup-variables.components'
import { TPatient } from 'src/types/patient'
import { useGenericFollowupForm } from './use-generic-followup-form.hook'
import { CurecallBanner, CurecallConfirmationModal } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import OptionalMedEventButtonCards from './med-events/optional-med-event-button-cards.component'
import CompletedMedEventCard from './med-events/completed-med-event-card.component'
import RecommendedMedEvent from './med-events/recommended-med-event.component'
import FollowupMedEventForm from './med-events/med-event-form.component'
import { TCompletedMedEvent, TFollowupFormContext } from './generic-followup-form.type'

export const FollowupFormContext = createContext<TFollowupFormContext>({})
export const useFollowupFormContext = () => useContext(FollowupFormContext)

interface Props {
  followup: TFollowup
  patient: TPatient
  patientFollowupIdEdited?: number
  onCancel?: () => void
}
const GenericFollowupFormComponent = ({ followup, patient, patientFollowupIdEdited, onCancel }: Props) => {
  const { t } = useTranslation()

  const {
    formContextValue,
    updatePatientVariableList,
    onSubmit,
    isDeleteMedEventModalOpened,
    onDeleteCard,
    closeDeleteModal,
    getDeleteModalContent,
    addNewFollowup,
  } = useGenericFollowupForm({
    followup,
    patient,
    patientFollowupIdEdited,
    onCancel,
  })

  const cannotAddMoreMedEvent =
    formContextValue?.requiredMedEvents.length === 0 &&
    formContextValue?.recommendedMedEvents.length === 0 &&
    formContextValue?.optionalMedEvents.length === 0

  return (
    <FollowupFormContext.Provider value={formContextValue}>
      <GenericFollowupHeader isEditMode={!!patientFollowupIdEdited} />
      <GenericFollowupVariables updatePatientVariableList={updatePatientVariableList} />
      <div className="c-flex c-flex-col c-gap-8">
        {formContextValue.completedMedEvents?.map((completedMedEvent, index) => (
          <CompletedMedEventCard
            completedMedEvent={completedMedEvent}
            onDelete={(medEventToDelete: TCompletedMedEvent) =>
              formContextValue.openConfirmModal?.(medEventToDelete)
            }
            key={`completed-${completedMedEvent?.medEventUI?.MedEvent?.id}-${
              completedMedEvent?.date?.toString() || ''
            }-${index}`}
          />
        ))}
        {formContextValue.selectedMedEvent && formContextValue.requiredMedEvents?.[0] && (
          <FollowupMedEventForm />
        )}
        {formContextValue.recommendedMedEvents?.map((recommendedMedEvent, index) => (
          <RecommendedMedEvent
            recommendedMedEvent={recommendedMedEvent}
            key={`recommanded-${recommendedMedEvent.MedEvent.id}-${index}`}
          />
        ))}
        {formContextValue.optionalMedEvents && formContextValue.optionalMedEvents.length > 0 && (
          <OptionalMedEventButtonCards />
        )}
      </div>
      {cannotAddMoreMedEvent && (
        <CurecallBanner
          appearance="INFO"
          className="c-mt-8"
          actionLabel={t('components.generic-followups-form.add-new-followup')}
          action={addNewFollowup}
        >
          {t('components.generic-followups-form.followup-complete')}
        </CurecallBanner>
      )}
      <GenericFollowupActions onSubmit={onSubmit} onCancelFollowupForm={onCancel} />
      <CurecallConfirmationModal
        isOpen={isDeleteMedEventModalOpened}
        onCancel={closeDeleteModal}
        onConfirm={onDeleteCard}
        confirmButtonLabel={t('common.actions.confirm')}
        cancelButtonLabel={t('common.actions.cancel')}
        title={t('components.generic-followups-form.confirm-medevent-to-delete')}
        color="ALERT"
      >
        {getDeleteModalContent()}
      </CurecallConfirmationModal>
    </FollowupFormContext.Provider>
  )
}

export default GenericFollowupFormComponent
